import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useLocation } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import './firebase'; // This will initialize Firebase
import Home from './pages/Home';
import OurStory from './pages/OurStory';
import Travel from './pages/Travel';
import Registry from './pages/Registry';
import Games from './pages/Games';
import PasswordGate from './components/PasswordGate';
import logoDH from './assets/logoDH.png';

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=Montserrat:wght@300;400;500&display=swap');

  html, body {
    margin: 0;
    padding: 0;
    background-color: #ffffff;
    color: #666;
    font-family: 'Montserrat', sans-serif;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: 'Cormorant Garamond', serif;
    font-weight: 400;
    color: #333;
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
  background-color: #ffffff;
`;

const Header = styled.header`
  text-align: center;
  padding: 2rem 0;
  margin-bottom: 2rem;
`;

const Monogram = styled.div`
  margin-bottom: 1rem;
  
  img {
    width: 250px;
    height: auto;
    filter: brightness(0);
  }
`;

const CoupleNames = styled.h1`
  font-size: 2.5rem;
  letter-spacing: 4px;
  margin: 0 0 1rem 0;
  text-transform: uppercase;
  color: #333;
`;

const WeddingInfo = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 0.9rem;
  letter-spacing: 2px;
  color: #666;
  text-transform: uppercase;
  margin-bottom: 1rem;
`;

const CountdownText = styled.div`
  font-family: 'Montserrat', sans-serif;
  font-size: 0.9rem;
  color: #666;
  margin-top: 0.5rem;
`;

const Navigation = styled.nav`
  display: flex;
  justify-content: center;
  gap: 2rem;
  padding: 1rem 0;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
  margin: 0 auto;

  @media (max-width: 768px) {
    justify-content: space-around;
    gap: 0;
  }

  a {
    text-decoration: none;
    color: #666;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    transition: color 0.3s ease;

    &:hover {
      color: #333;
    }
  }
`;

// Placeholder components for new routes
const ThingsToDo = () => (
  <div className="container">
    <h1>Things to Do</h1>
    <p>Coming soon...</p>
  </div>
);

function App() {
  const [daysToGo, setDaysToGo] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const weddingDate = new Date('2026-10-11T00:00:00');

  useEffect(() => {
    // Check if already authenticated
    const verified = localStorage.getItem('passwordVerified');
    if (verified === 'true') {
      setIsAuthenticated(true);
    }

    const calculateDaysToGo = () => {
      const today = new Date();
      const timeDiff = weddingDate.getTime() - today.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      setDaysToGo(daysDiff);
    };

    // Calculate initially
    calculateDaysToGo();

    // Update every day at midnight
    const now = new Date();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const timeUntilMidnight = tomorrow.getTime() - now.getTime();

    // First timeout to sync with midnight
    const initialTimeout = setTimeout(() => {
      calculateDaysToGo();
      // Then set up daily interval
      const interval = setInterval(calculateDaysToGo, 24 * 60 * 60 * 1000);
      return () => clearInterval(interval);
    }, timeUntilMidnight);

    return () => clearTimeout(initialTimeout);
  }, []);

  if (!isAuthenticated) {
    return <PasswordGate onCorrectPassword={() => setIsAuthenticated(true)} />;
  }

  return (
    <Router>
      <GlobalStyle />
      <AppContainer>
        <Header>
          <Monogram>
            <img src={logoDH} alt="H & D Monogram" />
          </Monogram>
          <CoupleNames>HANA & DAVID</CoupleNames>
          <WeddingInfo>OCTOBER 11, 2026 • WESTCHESTER, NY</WeddingInfo>
          <CountdownText>{daysToGo} DAYS TO GO!</CountdownText>
        </Header>
        <Navigation>
          <NavLink to="/" end>Home</NavLink>
          <NavLink to="/our-story">Our Story</NavLink>
          <NavLink to="/travel">Travel</NavLink>
          <NavLink to="/registry">Registry</NavLink>
          <NavLink to="/games">Games</NavLink>
        </Navigation>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/our-story" element={<OurStory />} />
          <Route path="/travel" element={<Travel />} />
          <Route path="/registry" element={<Registry />} />
          <Route path="/games/*" element={<Games />} />
        </Routes>
      </AppContainer>
    </Router>
  );
}

export default App;
